import React, { useEffect, useState, useMemo, useContext } from "react";
import { RouteComponentProps, Redirect } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionButton,
  GenericDetailsCard,
  GenericDetailsCardComponentEnum,
  LoadingPopup,
  B2BSpinner,
  useDeviceTypes,
  GenericInfoPopup,
  Icon,
  IconName,
  paymentTextConstants,
  PricingBreakdown,
  roundToTwoDecimals,
  AirCfarHtsMerchandising,
  formatInterval,
} from "halifax";
import {
  CallState,
  CfarEntryPointVariantEnum,
  PaymentV2Enum,
  PaymentV2,
  UserCard,
  Rewards,
  TripSlice,
  AncillaryFetchOfferRequest,
  ShopFilter,
  REVIEW_DETAILS_CFAR_POST_BOOKING,
  getHopperFareRatingName,
} from "redmond";
import { Prices } from "@b2bportal/air-shopping-api";
import {
  IPricingLineItem,
  ISummaryLineItem,
} from "halifax/build/PriceBreakdown";
import { useIsTravelApp, appendIsTravelAppParam } from "@capone/common";
import { CfarPostBookingConfirmAndBookConnectorProps } from "./container";
import { PaymentCard } from "../../../common/PaymentCard";
import { config } from "../../../../api/config";
import { ClientContext } from "../../../../App";
import { CfarPostBookingOfferKindEnum } from "../../reducer";
import { ITINERARY_ID_QUERY_PARAM, PATH_FLIGHTS } from "../../constants";
import { useHtsMerchandisingScripts } from "../../../shop/components/addOnComponents/hooks/useHtsMerchandisingScripts";
import { getBookingConfirmation } from "../../../book/components/BookingSuccessModal/bookingConfirmationStorage";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { PATH_CFAR_OFFER_CONFIRMATION } from "../../../../utils/urlPaths";
import {
  AVAILABLE,
  getExperimentVariant,
  POST_BOOKING_CFAR_FARE_OPTIMIZATION_ENTRY_POINT,
  useExperiments,
} from "../../../../context/experiments";
import "./styles.scss";

export interface ICfarPostBookingConfirmAndBookProps
  extends CfarPostBookingConfirmAndBookConnectorProps,
    RouteComponentProps {}

type AmountType<T extends PaymentV2> = { model: T; fiatValue: number };

const DATE_FORMAT = "ddd, MMM D";
const TIME_FORMAT = "h:mm A";
const UPCOMING_TRIP = "Your upcoming trip";
const CFAR_ITINERARY = "Cancel for any reason will be added to this itinerary.";
const CFAR_HEADER_TITLE = "Cancel for any reason";
const CFAR_HEADER_SUBHEADER = "Add flexibility for your trip";

export const CfarPostBookingConfirmAndBook = (
  props: ICfarPostBookingConfirmAndBookProps,
) => {
  const {
    history,
    cfarOffer,
    cfarPostBookingOfferState,
    cfarPostBookingFetchOfferRequest,
    isPostBookingCfarEnabled,
    fetchCfarPostBookingOffer,
    fetchTravelWalletSummary,
    purchaseCfarPostBookingOffer,
    cfarPostBookingPurchaseCallState,
    cfarPostBookingPurchaseResponse,
    paymentMethods,
    rewardsAccounts,
    listPaymentMethods,
    listPaymentMethodCallState,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    selectedRewardsPaymentAccount,
    selectedAccountReferenceId,
    fetchRewardsAccounts,
    rewardsPaymentInFiatCurrency,
    rewardsPaymentInRewardsCurrency,
    creditsToApply,
    creditToApply,
    credits,
    setCreditToApply,
  } = props;

  const { matchesMobile } = useDeviceTypes();
  const { isTravelApp } = useIsTravelApp();
  const clientContext = useContext(ClientContext);

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [isNotCapOneAccount, setIsNotCapOneAccount] = useState(false);

  const expState = useExperiments();

  const postBookingCfarFoEntryPointExp = getExperimentVariant(
    expState.experiments,
    POST_BOOKING_CFAR_FARE_OPTIMIZATION_ENTRY_POINT,
  );

  const isPostBookingCfarFoEntryPointEnabled = useMemo(
    () => postBookingCfarFoEntryPointExp === AVAILABLE,
    [postBookingCfarFoEntryPointExp],
  );

  useEffect(() => {
    if (cfarPostBookingPurchaseCallState === CallState.Failed) {
      setOpenErrorModal(true);
    }
  }, [cfarPostBookingPurchaseCallState]);

  const savedConfirmation = useMemo(() => getBookingConfirmation(), []);
  const tripDetails = savedConfirmation?.tripDetails;

  const {
    htsCfarComplete,
    htsCfarLoadingError,
    onHtsCfarLoadSuccess,
    onHtsCfarLoadError,
  } = useHtsMerchandisingScripts({
    isHtsCfar: true,
    isHtsDisruption: false,
    isCfarAvailable: true,
    isDisruptionProtectionAvailable: false,
    resetCfarSelection: () => {},
    resetDisruptionSelection: () => {},
  });

  useEffect(() => {
    if (listPaymentMethodCallState === CallState.NotCalled) {
      listPaymentMethods();
    }
    fetchRewardsAccounts(true);
    fetchTravelWalletSummary();
  }, []);

  useEffect(() => {
    if (credits) {
      setCreditToApply(credits);
    }
  }, [credits]);

  useEffect(() => {
    if (
      cfarPostBookingOfferState.state !== CfarPostBookingOfferKindEnum.NotCalled
    )
      return;
    const params = new URLSearchParams(history.location.search);
    const itineraryId = params.get(ITINERARY_ID_QUERY_PARAM);
    const pnr = params.get("pnr") ?? undefined;

    const fetchOfferRequest: AncillaryFetchOfferRequest | undefined =
      cfarPostBookingFetchOfferRequest ??
      (params.get("tripId") && params.get("fareId")
        ? {
            tripId: params.get("tripId")!,
            fareId: params.get("fareId")!,
            tripFilter:
              (params.get("tripFilter") as ShopFilter) ?? ShopFilter.NoFilter,
            passengers: JSON.parse(params.get("passengers") ?? "{}"),
          }
        : undefined);

    if (itineraryId) {
      fetchCfarPostBookingOffer(itineraryId, pnr, fetchOfferRequest);
    }
  }, [cfarPostBookingOfferState.state]);

  useEffect(() => {
    if (selectedPaymentMethodId || paymentMethods.length === 0) return;
    let matched = false;
    rewardsAccounts.forEach((account) => {
      if (matched) return;
      const match = paymentMethods.find(
        (pm) =>
          pm.last4 === account.lastFour ||
          account.lastFourVirtualCardNumbers?.includes(pm.last4 || ""),
      );
      if (!match) return;
      setSelectedPaymentMethodId({
        paymentMethodId: match.id,
        accountId: account.accountReferenceId,
      });
      matched = true;
    });
  }, [selectedPaymentMethodId, paymentMethods.length, rewardsAccounts.length]);

  useEffect(() => {
    if (!cfarOffer) return;
    trackEvent({
      eventName: REVIEW_DETAILS_CFAR_POST_BOOKING,
      properties: {
        cfar_per_pax: cfarOffer.premiumPerPax.fiat.value,
        post_booking_cfar_choice: 1, // 0 would be if user doesn't select. If we're making it to checkout, user selecteed cfar
      },
    });
  }, [!!cfarOffer]);

  const premiumFiat = cfarOffer?.premiumPerPax.fiat;
  const passengerNames = useMemo(() => {
    const itinerary = savedConfirmation?.itinerary;
    if (!itinerary) return [];
    const { alone, withLapInfants } = itinerary.passengers;
    return [
      ...alone.map((p) => `${p.person.givenName} ${p.person.surname}`),
      ...withLapInfants.map(
        (entry) =>
          `${entry.adult.person.givenName} ${entry.adult.person.surname}`,
      ),
    ];
  }, [savedConfirmation?.itinerary]);
  const passengerCount = passengerNames.length || 1;
  const totalAmount = roundToTwoDecimals(
    (premiumFiat?.value ?? 0) * passengerCount,
  );
  const currencyCode = premiumFiat?.currencyCode ?? "USD";
  const currencySymbol = premiumFiat?.currencySymbol ?? "$";

  const creditToApplyAmount = useMemo(() => {
    if (!creditsToApply || totalAmount <= 0) return 0;
    const creditAmount = Math.abs(creditsToApply.amount.amount);
    return Math.min(creditAmount, totalAmount);
  }, [creditsToApply, totalAmount]);

  const amounts: AmountType<PaymentV2>[] = [];

  if (creditToApply && creditToApplyAmount > 0) {
    amounts.push({
      model: {
        PaymentV2: PaymentV2Enum.TravelWalletCredit,
        offerId: creditToApply.id,
        description: "TravelWalletCredit",
        paymentAmount: {
          fiatValue: {
            amount: roundToTwoDecimals(creditToApplyAmount),
            currency: creditToApply.amount.currency,
          },
        },
      },
      fiatValue: creditToApplyAmount,
    });
  }

  function totalReadyToPay() {
    return (
      Math.round(amounts.reduce((acc, obj) => acc + obj.fiatValue, 0) * 100) /
      100
    );
  }

  function amountRemaining() {
    return Math.round(Math.max(0, totalAmount - totalReadyToPay()) * 100) / 100;
  }

  const doesCreditCoverWholeAmount = amountRemaining() <= 0;

  if (
    !doesCreditCoverWholeAmount &&
    rewardsPaymentInFiatCurrency &&
    rewardsPaymentInRewardsCurrency &&
    selectedRewardsPaymentAccount
  ) {
    const splitRewardsPayment: Rewards = {
      paymentAmount: {
        rewardsAccountId: selectedRewardsPaymentAccount.accountReferenceId,
        fiatValue: {
          amount: roundToTwoDecimals(rewardsPaymentInFiatCurrency.value),
          currency: rewardsPaymentInFiatCurrency.currencyCode,
        },
        rewardsPrice: {
          value: roundToTwoDecimals(rewardsPaymentInRewardsCurrency.value),
          currency: rewardsPaymentInRewardsCurrency.currency,
        },
      },
      PaymentV2: PaymentV2Enum.Rewards,
    };
    amounts.push({
      model: splitRewardsPayment,
      fiatValue: rewardsPaymentInFiatCurrency.value,
    });
  }

  if (
    selectedPaymentMethodId &&
    selectedPaymentMethodId !== "" &&
    amountRemaining() > 0
  ) {
    const amount = amountRemaining();
    const splitUserCardPayment: UserCard & { last4?: string } = {
      paymentId: selectedPaymentMethodId,
      accountReferenceId: selectedRewardsPaymentAccountId ?? undefined,
      paymentAmount: {
        currency: currencyCode,
        amount: roundToTwoDecimals(amount),
      },
      PaymentV2: PaymentV2Enum.UserCard,
    };
    amounts.push({
      model: splitUserCardPayment,
      fiatValue: amount,
    });
  }

  const totalAmountRemaining = amountRemaining();

  const canPurchase = totalAmountRemaining <= 0;

  const handlePurchase = () => {
    if (!cfarOffer) return;
    const params = new URLSearchParams(history.location.search);
    const itineraryId = params.get(ITINERARY_ID_QUERY_PARAM) ?? "";
    const fareDetails = tripDetails?.fareDetails[0];
    const slice = tripDetails?.slices[0];
    purchaseCfarPostBookingOffer({
      request: {
        itineraryId,
        payments: amounts.map((a) => a.model),
        accountReferenceId: selectedRewardsPaymentAccountId ?? undefined,
        cfarOfferId: cfarOffer.id,
      },
      trackingProperties: {
        total_card_amount: ccPaymentAmount ?? 0,
        advance: slice
          ? dayjs(slice.departureTime).diff(dayjs(), "day")
          : undefined,
        destination_country_code: savedConfirmation?.destinationCountryCode,
        fare_class: fareDetails
          ? getHopperFareRatingName(fareDetails.slices[0]?.fareShelf?.rating)
          : undefined,
        multi_ticket_type: savedConfirmation?.itinerary.multiTicketType,
      },
    });
  };

  if (cfarPostBookingPurchaseResponse) {
    history.replace(
      appendIsTravelAppParam(PATH_CFAR_OFFER_CONFIRMATION, isTravelApp),
    );
    return null;
  }

  if (isPostBookingCfarEnabled === false) {
    return <Redirect to={PATH_FLIGHTS} />;
  }

  if (
    cfarPostBookingOfferState.state ===
      CfarPostBookingOfferKindEnum.NotCalled ||
    cfarPostBookingOfferState.state === CfarPostBookingOfferKindEnum.Fetching
  ) {
    return (
      <LoadingPopup
        indicatorSize="small"
        indicator={B2BSpinner}
        open
        message="Loading offer"
        loadingIndicatorTextVariant={matchesMobile ? undefined : "h2"}
        classes={[
          "cfar-post-booking-loading-modal",
          ...(matchesMobile ? ["mobile"] : []),
        ]}
      />
    );
  }

  if (cfarPostBookingPurchaseCallState === CallState.InProcess) {
    return (
      <LoadingPopup
        indicatorSize="small"
        indicator={B2BSpinner}
        open
        message="Purchasing"
        loadingIndicatorTextVariant={matchesMobile ? undefined : "h2"}
        classes={[
          "cfar-post-booking-loading-modal",
          ...(matchesMobile ? ["mobile"] : []),
        ]}
      />
    );
  }

  if (!cfarOffer) {
    return null;
  }

  const rewardsPremiumAmount = selectedAccountReferenceId
    ? cfarOffer.premiumPerPax.rewards[selectedAccountReferenceId]
    : undefined;
  const rewardsType =
    rewardsPremiumAmount &&
    rewardsPremiumAmount.value !== 0 &&
    cfarOffer.premiumPerPax.fiat.value !== 0
      ? rewardsPremiumAmount.currency.toLowerCase()
      : undefined;
  const rewardsConversion =
    rewardsType && rewardsPremiumAmount
      ? rewardsPremiumAmount.value / cfarOffer.premiumPerPax.fiat.value
      : undefined;

  const purchaseEntryPoint =
    isPostBookingCfarFoEntryPointEnabled &&
    cfarOffer.entryPointVariant?.CfarEntryPointVariant ===
      CfarEntryPointVariantEnum.PostBookingFo
      ? "postbooking-purchase-fo"
      : "postbooking-purchase";

  const merchandisingUrl = cfarOffer.merchandisingUrl
    ? new URL(`${cfarOffer.merchandisingUrl}&entryPoint=${purchaseEntryPoint}`)
    : null;

  if (merchandisingUrl && rewardsType && rewardsConversion) {
    merchandisingUrl.searchParams.set("rewardsType", rewardsType);
    merchandisingUrl.searchParams.set(
      "rewardsConversion",
      rewardsConversion.toString(),
    );
  }

  const totalPrice: Prices = {
    fiat: { currencyCode, currencySymbol, value: totalAmount },
    accountSpecific: Object.fromEntries(
      Object.entries(cfarOffer?.premiumPerPax.rewards ?? {}).map(
        ([accountId, rewardsPrice]) => [
          accountId,
          { ...rewardsPrice, value: rewardsPrice.value * passengerCount },
        ],
      ),
    ),
  };

  const pricingItems: IPricingLineItem[] = passengerNames.map(
    (passengerName) => ({
      lineTitle: passengerName,
      cfarFee: premiumFiat?.value,
      skipIfPriceUnavailable: true,
    }),
  );

  const ccPaymentAmount = amounts.find(
    (a) => a.model.PaymentV2 === PaymentV2Enum.UserCard,
  )?.fiatValue;

  const totalRewardsPrice =
    cfarOffer?.premiumPerPax.rewards[selectedRewardsPaymentAccountId ?? ""];

  const summaryLineItems: ISummaryLineItem[] = [
    {
      type: "total",
      fiatPrice: { currencyCode, currencySymbol, value: totalAmount },
      rewardsPrice: totalRewardsPrice
        ? {
            ...totalRewardsPrice,
            value: totalRewardsPrice.value * passengerCount,
          }
        : undefined,
    },
  ];

  if (creditsToApply && creditToApplyAmount > 0) {
    summaryLineItems.push({
      type: "custom",
      icon: "piggy-bank-icon",
      label: "Travel credits applied:",
      fiatPrice: { currencyCode, currencySymbol, value: -creditToApplyAmount },
      className: "travel-wallet-line",
      travelCreditBreakdown: [],
    });
  }

  if (
    rewardsPaymentInFiatCurrency &&
    selectedRewardsPaymentAccount &&
    rewardsPaymentInRewardsCurrency
  ) {
    summaryLineItems.push({
      type: "rewards",
      fiatPrice: rewardsPaymentInFiatCurrency,
      rewardsAccountName: selectedRewardsPaymentAccount.productDisplayName,
      rewardsPrice: rewardsPaymentInRewardsCurrency,
    });
  }

  summaryLineItems.push({
    type: "payment",
    fiatPrice: {
      currencyCode,
      currencySymbol,
      value: ccPaymentAmount ?? totalAmountRemaining,
    },
    lastFour: paymentMethods.find((cc) => cc.id === selectedPaymentMethodId)
      ?.last4,
  });

  const getSliceHeading = (slice: TripSlice, index: number, total: number) => {
    const departure = dayjs(slice.departureTime).format(DATE_FORMAT);
    if (total <= 2) {
      const direction = total === 1 || index === 0 ? "Outbound" : "Return";
      return (
        <>
          <span className="bold">{direction}</span> to {slice.destinationName} (
          {slice.destinationCode}) on {departure}
        </>
      );
    }
    return (
      <>
        <span className="bold">
          Flight {index + 1}: {slice.originName} ({slice.originCode}) to{" "}
          {slice.destinationName} ({slice.destinationCode})
        </span>{" "}
        on {departure}
      </>
    );
  };

  const getTripSection = () => {
    if (!tripDetails?.slices?.length) return null;
    const { slices } = tripDetails;
    const total = slices.length;

    const renderSlice = (slice: TripSlice, index: number) => (
      <div
        key={slice.originCode + slice.destinationCode + slice.departureTime}
        className={clsx(
          "trip-section-box",
          matchesMobile && "flex-row",
          matchesMobile && "space-between",
        )}
      >
        <Box>
          <div className="trip-segment-heading">
            {getSliceHeading(slice, index, total)}
          </div>
          <div className="flex-row">
            <div className="flex-1">
              {dayjs(slice.departureTime).format(TIME_FORMAT)} –{" "}
              {dayjs(slice.arrivalTime).format(TIME_FORMAT)}
            </div>
            <div className="flex-1">
              {formatInterval(slice.totalDurationMinutes ?? 0)}
            </div>
          </div>
          <div className={clsx("secondary-copy", "flex-row")}>
            <div className="flex-1">{slice.segmentDetails[0]?.airlineName}</div>
            <div className="flex-1">
              {slice.stops === 0
                ? "Nonstop"
                : `${slice.stops} stop${slice.stops > 1 ? "s" : ""}`}
            </div>
          </div>
        </Box>
      </div>
    );

    if (total <= 2) {
      return (
        <div className="trip-section-container">
          {slices.map((slice, i) => renderSlice(slice, i))}
        </div>
      );
    }

    return (
      <div className="multi-leg-trip">
        {slices.map((slice, i) => (
          <div
            key={slice.originCode + slice.destinationCode + slice.departureTime}
            className="trip-section-container"
          >
            {renderSlice(slice, i)}
          </div>
        ))}
      </div>
    );
  };

  const renderHtsMerchandising = () => {
    if (!merchandisingUrl) return null;
    return (
      <div className="cfar-merchandising-container">
        <AirCfarHtsMerchandising
          merchandisingUrl={merchandisingUrl}
          onSelect={() => {}}
          onLoadSuccess={onHtsCfarLoadSuccess}
          onLoadError={onHtsCfarLoadError}
          isLoading={!htsCfarComplete}
          isError={htsCfarLoadingError}
          offerId={cfarOffer.id.productId}
          entryPoint={purchaseEntryPoint}
        />
      </div>
    );
  };

  const renderUpcomingTrip = () => (
    <GenericDetailsCard
      contentClassName="cfar-post-booking-confirm-and-book-card"
      openModal={false}
      contentOnly
      mainContent={[
        {
          copy: UPCOMING_TRIP,
          type: "primary",
          typographyVariant: matchesMobile ? undefined : "h3",
          component: GenericDetailsCardComponentEnum.GenericCopy,
        },
        {
          copy: CFAR_ITINERARY,
          type: "subtitle",
          component: GenericDetailsCardComponentEnum.GenericCopy,
        },
        {
          content: getTripSection() ?? <span />,
          component: GenericDetailsCardComponentEnum.Custom,
        },
      ]}
      isMobile={matchesMobile}
    />
  );

  const renderPaymentCard = () => (
    <GenericDetailsCard
      contentClassName="cfar-post-booking-confirm-and-book-card"
      openModal={false}
      contentOnly
      mainContent={[
        {
          content: (
            <PaymentCard
              {...{
                ...props,
                rewardsTitleId: "",
                rewardsSubtitleId: "",
                total: totalPrice,
                type: "flight",
                isTravelCreditPaymentOnly: doesCreditCoverWholeAmount,
              }}
            />
          ),
          component: GenericDetailsCardComponentEnum.Custom,
        },
      ]}
      isMobile={matchesMobile}
    />
  );

  const renderCheckoutBreakdown = () => (
    <Box className="checkout-template-column-right-wrapper">
      <Box className="checkout-template-card-content-container pricing-breakdown">
        <Box className="pricing-breakdown-header-container">
          <Typography className="header" variant="h4">
            Checkout Breakdown
          </Typography>
        </Box>
        <Box className="payment-break-down">
          <PricingBreakdown
            className="price-breakdown"
            pricingItems={pricingItems}
            summaryItems={summaryLineItems}
            currencySymbol={currencySymbol}
            currencyCode={currencyCode}
            isVITripSelected={false}
          />
        </Box>
        <Box className="confirm-book-button-container">
          <ActionButton
            className="confirm-book-button"
            defaultStyle="h4r-primary"
            message="Add to your trip and pay"
            disabled={!canPurchase}
            onClick={handlePurchase}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderLogoHeader = () => (
    <Box
      className={clsx("post-booking-offer-header-container", {
        isMobile: matchesMobile,
      })}
    >
      <Box
        className="logo-container"
        onClick={() => history.push({ pathname: PATH_FLIGHTS })}
      >
        {clientContext.logo}
      </Box>
      <Box className="cfar-header-text-container">
        <Typography className="title" variant="body1">
          {CFAR_HEADER_TITLE}
        </Typography>
        <Typography className="subheader" variant="body2">
          {CFAR_HEADER_SUBHEADER}
        </Typography>
      </Box>
    </Box>
  );

  const goBack = () => (
    <Box
      className={clsx("post-booking-offer-header-container", {
        isMobile: matchesMobile,
      })}
    >
      <Box className="back-button-container" onClick={() => history.goBack()}>
        <Icon name={IconName.ChevronLeftBlueThin} />
        <span className="text-container">Back</span>
      </Box>
    </Box>
  );

  const renderMobile = () => (
    <Box
      className={clsx("cfar-post-booking-confirm-and-book-root", {
        isMobile: matchesMobile,
      })}
    >
      {renderHtsMerchandising() ?? null}
      <Box className="jump-to-checkout">
        <ActionButton
          defaultStyle="h4r-secondary"
          message={
            <Box className={clsx("flex-row", "align-center")}>
              <Icon name={IconName.ChevronLeftBlueThin} />
              <span className="text">Jump to checkout</span>
            </Box>
          }
          onClick={() =>
            document
              .getElementById("cfar-checkout-breakdown")
              ?.scrollIntoView({ behavior: "smooth" })
          }
        />
      </Box>
      {renderUpcomingTrip()}
      {renderPaymentCard()}
      <Box
        id="cfar-checkout-breakdown"
        className="cfar-post-booking-purchase-section"
      >
        <PricingBreakdown
          className="price-breakdown"
          pricingItems={pricingItems}
          summaryItems={summaryLineItems}
          currencySymbol={currencySymbol}
          currencyCode={currencyCode}
          isVITripSelected={false}
        />
        <ActionButton
          defaultStyle="h4r-primary"
          message={`Add and pay for ${currencySymbol}${totalAmount.toFixed(2)}`}
          disabled={!canPurchase}
          onClick={handlePurchase}
        />
      </Box>
    </Box>
  );

  const renderDesktop = () => (
    <Box className="cfar-post-booking-confirm-and-book-root">
      <Typography variant="h2" className="postbooking-header-container">
        View details and confirm your purchase
      </Typography>

      <Box className="flex-row">
        <Box className="primary-col">
          {renderHtsMerchandising()}
          {renderUpcomingTrip()}
          {renderPaymentCard()}
        </Box>
        <Box className="secondary-col">{renderCheckoutBreakdown()}</Box>
      </Box>
    </Box>
  );

  return (
    <Box
      className={clsx("cfar-post-booking-root", {
        "cfar-post-booking-root-mobile": matchesMobile,
      })}
    >
      {!matchesMobile && renderLogoHeader()}
      {(matchesMobile ? !isTravelApp : true) && goBack()}
      {matchesMobile ? renderMobile() : renderDesktop()}
      {openErrorModal && (
        <GenericInfoPopup
          open={openErrorModal}
          image={
            <Icon
              name={
                isNotCapOneAccount
                  ? IconName.ErrorState
                  : IconName.UnableToProcess
              }
            />
          }
          title={
            isNotCapOneAccount
              ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_TITLE
              : paymentTextConstants.UNABLED_TO_ADD_PAYMENT
          }
          subtitle={
            isNotCapOneAccount
              ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_SUBTITLE(
                  config.TENANT,
                )
              : paymentTextConstants.ADD_PAYMENT_AGAIN
          }
          buttons={[
            {
              buttonText: "Close",
              onClick: () => {
                setOpenErrorModal(false);
                setIsNotCapOneAccount(false);
              },
              defaultStyle: "h4r-primary",
            },
          ]}
          isMobile={matchesMobile}
        />
      )}
      {listPaymentMethodCallState === CallState.InProcess && (
        <LoadingPopup
          indicatorSize="small"
          indicator={B2BSpinner}
          open
          message="Loading Offer"
          loadingIndicatorTextVariant={matchesMobile ? undefined : "h2"}
          classes={[
            "cfar-post-booking-loading-modal",
            ...(matchesMobile ? ["mobile"] : []),
          ]}
        />
      )}
    </Box>
  );
};
